import { useCallback } from "react";

import { Action, Module, Run, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";

import { isRunDiscardable } from "../useDiscardRun/accessValidation";
import useHasEntityWriteAccess from "./useHasEntityWriteAccess";

const DISCARD_ACCESS_TYPES = [Action.RunDiscard];

const hasDiscardAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(DISCARD_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can discard a run.
 * A user needs write access to discard a run.
 * A user can only discard a run when it's in a discardable state (Ready, Queued, or Unconfirmed).
 * @returns Function that takes a run and optional stack or module and returns a AccessResult indicating if user can discard it
 */
const useCanDiscardRun = (): ((
  run: Run,
  entity?: Partial<Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">>
) => AccessResult) => {
  const hasEntityWriteAccess = useHasEntityWriteAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (run, entity) => {
      if (!isRunDiscardable(run)) {
        return { result: false };
      }

      if (!entity) {
        return { result: false };
      }

      const hasAccess =
        hasEntityWriteAccess(entity) ||
        hasDiscardAccess(viewer, entity.spaceDetails?.id || entity.space);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer, hasEntityWriteAccess]
  );
};

export default useCanDiscardRun;
